import React, { CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import {
    AlertCircle, ArrowLeft, Check, ClipboardPaste, Loader2, Plus, Save, Trash2,
} from 'lucide-react';
import { useDownloadAttachment, useLoadHttpClientEmpty } from '../../../App.hooks';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type Row = Record<string, string | null>;

const DYNAMIC_DATA_TYPE = 'Standards';
const DYNAMIC_DATA_NAME = 'Parameter Mapping';

/* ------------------------------------------------------------------ */
/*  GraphQL                                                            */
/* ------------------------------------------------------------------ */

const GET_PROJECT_PARAM_MAPPING_DATA = gql`
    query getProjectParamMappingData($id: String) {
        Project(id: $id) {
            dynamicData { id name type data setData }
        }
    }
`;

const SET_PROJECT_DYNAMIC_DATA = gql`
    mutation setParamMappingDynamicData($projectId: String, $data: DynamicDataInput) {
        Project(id: $projectId) { setDynamicData(data: $data) }
    }
`;

/* ------------------------------------------------------------------ */
/*  Paste modal                                                        */
/* ------------------------------------------------------------------ */

type PasteModalProps = {
    existingColumns: string[];
    onImport: (columns: string[], rows: Row[], mode: 'replace' | 'append') => void;
    onClose: () => void;
};

const PasteModal = ({ existingColumns, onImport, onClose }: PasteModalProps) => {
    const [raw, setRaw] = useState('');

    const parsed = useMemo(() => {
        if (!raw.trim()) return null;
        const lines = raw.trim().split('\n').map(l => l.split('\t'));
        if (lines.length < 2) return null;
        const headers = lines[0].map(h => h.trim()).filter(Boolean);
        if (headers.length === 0) return null;
        const rows: Row[] = lines.slice(1).map(cells => {
            const row: Row = {};
            headers.forEach((h, i) => { row[h] = (cells[i] ?? '').trim() || null; });
            return row;
        });
        return { headers, rows };
    }, [raw]);

    const canAppend = existingColumns.length > 0;

    return (
        <div
            className="fixed inset-0 flex items-center justify-center"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 50 }}
            onClick={e => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div
                className="card rounded-xl shadow-xl flex flex-col"
                style={{ width: 700, maxHeight: '80vh', border: '1px solid var(--border-default)' }}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3 border-b flex-shrink-0" style={{ borderColor: 'var(--border-default)' }}>
                    <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                        Paste from Excel
                    </span>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', fontSize: 18, lineHeight: 1 }}>×</button>
                </div>

                {/* Paste area */}
                <div className="px-5 py-4 flex-shrink-0">
                    <p className="text-xs mb-2" style={{ color: 'var(--text-secondary)' }}>
                        Copy a range from Excel — the <strong>first row must be column headers</strong>.
                    </p>
                    <textarea
                        autoFocus
                        className="w-full input-base"
                        style={{ height: 100, fontFamily: 'monospace', fontSize: 11, resize: 'vertical' }}
                        placeholder="Paste Excel data here…"
                        value={raw}
                        onChange={e => setRaw(e.target.value)}
                    />
                </div>

                {/* Preview */}
                {parsed && (
                    <div className="flex-1 overflow-auto px-5 pb-2 min-h-0">
                        <p className="text-xs font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                            Preview — {parsed.rows.length} row{parsed.rows.length !== 1 ? 's' : ''}, {parsed.headers.length} column{parsed.headers.length !== 1 ? 's' : ''}
                        </p>
                        <div className="overflow-auto rounded border" style={{ borderColor: 'var(--border-default)', maxHeight: 220 }}>
                            <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ backgroundColor: 'var(--surface-1, var(--surface-100))' }}>
                                        {parsed.headers.map(h => (
                                            <th
                                                key={h}
                                                className="text-left px-3 py-2 font-semibold border-b"
                                                style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)', whiteSpace: 'nowrap' }}
                                            >
                                                {h}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {parsed.rows.slice(0, 6).map((row, i) => (
                                        <tr key={i} style={{ borderBottom: '1px solid var(--border-subtle, var(--border-default))' }}>
                                            {parsed.headers.map(h => (
                                                <td key={h} className="px-3 py-1.5" style={{ color: 'var(--text-primary)' }}>
                                                    {row[h] ?? ''}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                    {parsed.rows.length > 6 && (
                                        <tr>
                                            <td colSpan={parsed.headers.length} className="px-3 py-2 text-center text-xs" style={{ color: 'var(--text-tertiary)' }}>
                                                + {parsed.rows.length - 6} more rows
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-5 py-3 border-t flex-shrink-0" style={{ borderColor: 'var(--border-default)' }}>
                    <button className="btn-secondary text-sm" onClick={onClose}>Cancel</button>
                    {canAppend && parsed && (
                        <button className="btn-secondary text-sm" onClick={() => onImport(parsed.headers, parsed.rows, 'append')}>
                            Append {parsed.rows.length} rows
                        </button>
                    )}
                    {parsed && (
                        <button className="btn-primary text-sm" onClick={() => onImport(parsed.headers, parsed.rows, 'replace')}>
                            Replace all ({parsed.rows.length} rows)
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

/* ------------------------------------------------------------------ */
/*  Table row (memoised for performance on large tables)              */
/* ------------------------------------------------------------------ */

type TableRowProps = {
    rowIdx: number;
    row: Row;
    columns: string[];
    onChange: (rowIdx: number, col: string, value: string) => void;
    onRemove: (idx: number) => void;
};

const TableRow = ({ rowIdx, row, columns, onChange, onRemove }: TableRowProps) => {
    const [hovered, setHovered] = useState(false);

    return (
        <tr
            style={{
                borderBottom: '1px solid var(--border-subtle, var(--border-default))',
                backgroundColor: hovered ? 'var(--bg-hover)' : 'transparent',
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            {columns.map(col => (
                <td
                    key={col}
                    style={{ padding: 0, borderRight: '1px solid var(--border-subtle, var(--border-default))' }}
                >
                    <input
                        type="text"
                        style={{
                            background: 'transparent',
                            border: 'none',
                            outline: 'none',
                            padding: '5px 12px',
                            fontSize: '0.8125rem',
                            color: 'var(--text-primary)',
                            width: '100%',
                            boxSizing: 'border-box',
                        }}
                        onFocus={e => { e.currentTarget.style.backgroundColor = 'var(--accent-soft)'; }}
                        onBlur={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                        value={row[col] ?? ''}
                        onChange={e => onChange(rowIdx, col, e.target.value)}
                    />
                </td>
            ))}
            <td style={{ width: 36, padding: '0 4px', textAlign: 'center' }}>
                <button
                    onClick={() => onRemove(rowIdx)}
                    title="Delete row"
                    style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: hovered ? 'var(--danger)' : 'transparent',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        padding: 4, borderRadius: 4, transition: 'color 0.1s',
                    }}
                >
                    <Trash2 size={13} />
                </button>
            </td>
        </tr>
    );
};

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

type ParameterMappingStandardProps = {
    projectId: string;
    style?: CSSProperties;
    onBack: () => void;
};

const ParameterMappingStandard = ({ projectId, style, onBack }: ParameterMappingStandardProps) => {
    const [rows, setRows] = useState<Row[]>([]);
    const [columns, setColumns] = useState<string[]>([]);
    const [isDirty, setIsDirty] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingBlob, setIsLoadingBlob] = useState(false);
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');
    const [showPasteModal, setShowPasteModal] = useState(false);
    const [filterText, setFilterText] = useState('');

    const download = useDownloadAttachment();
    const httpClient = useLoadHttpClientEmpty();

    /* ── Query ──────────────────────────────────────────────────── */
    const { data: queryData, loading: queryLoading, refetch } = useQuery(
        GET_PROJECT_PARAM_MAPPING_DATA,
        { variables: { id: projectId }, skip: !projectId, fetchPolicy: 'network-only' },
    );

    const [setDynamicDataMutation] = useMutation(SET_PROJECT_DYNAMIC_DATA);

    const existingEntry = useMemo(() => {
        const entries: any[] = queryData?.Project?.dynamicData ?? [];
        return entries.find(
            d => d?.type === DYNAMIC_DATA_TYPE && d?.name === DYNAMIC_DATA_NAME,
        ) as { id: string; name: string; type: string; data?: string; setData?: string } | undefined;
    }, [queryData]);

    /* ── Load blob ──────────────────────────────────────────────── */
    useEffect(() => {
        if (queryLoading || !existingEntry?.data) return;
        setIsLoadingBlob(true);
        download(existingEntry.data)
            .then(raw => {
                const str = typeof raw === 'string' ? raw : JSON.stringify(raw);
                const parsed = JSON.parse(str) as Row[];
                if (Array.isArray(parsed) && parsed.length > 0) {
                    setRows(parsed);
                    setColumns(Object.keys(parsed[0]));
                }
            })
            .catch(() => {})
            .finally(() => setIsLoadingBlob(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingEntry?.data, queryLoading]);

    /* ── Helpers ────────────────────────────────────────────────── */
    const handlePasteImport = useCallback((cols: string[], newRows: Row[], mode: 'replace' | 'append') => {
        if (mode === 'replace') {
            setColumns(cols);
            setRows(newRows);
        } else {
            const mergedCols = Array.from(new Set(columns.concat(cols)));
            setColumns(mergedCols);
            setRows(prev => prev.concat(newRows));
        }
        setIsDirty(true);
        setShowPasteModal(false);
    }, [columns]);

    const handleCellChange = useCallback((rowIdx: number, col: string, value: string) => {
        setRows(prev => {
            const next = prev.slice();
            next[rowIdx] = { ...next[rowIdx], [col]: value || null };
            return next;
        });
        setIsDirty(true);
    }, []);

    const handleAddRow = useCallback(() => {
        const blank: Row = {};
        columns.forEach(c => { blank[c] = null; });
        setRows(prev => prev.concat([blank]));
        setIsDirty(true);
    }, [columns]);

    const handleRemoveRow = useCallback((idx: number) => {
        setRows(prev => prev.filter((_, i) => i !== idx));
        setIsDirty(true);
    }, []);

    /* ── Save ───────────────────────────────────────────────────── */
    const handleSave = useCallback(async () => {
        setIsSaving(true);
        setSaveStatus('idle');
        try {
            if (!existingEntry) {
                await setDynamicDataMutation({
                    variables: {
                        projectId,
                        data: { name: DYNAMIC_DATA_NAME, type: DYNAMIC_DATA_TYPE, description: 'Parameter mapping table' },
                    },
                });
            }
            const refetched = await refetch();
            const entry: any = (refetched.data?.Project?.dynamicData ?? []).find(
                (d: any) => d?.type === DYNAMIC_DATA_TYPE && d?.name === DYNAMIC_DATA_NAME,
            );
            if (!entry?.setData) throw new Error('No upload URL returned');
            await httpClient.put(entry.setData, JSON.stringify(rows), {
                headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': 'application/json', 'Cache-Control': 'no-cache, no-store, must-revalidate' },
            });
            setIsDirty(false);
            setSaveStatus('saved');
            setTimeout(() => setSaveStatus('idle'), 3000);
        } catch {
            setSaveStatus('error');
            setTimeout(() => setSaveStatus('idle'), 4000);
        } finally {
            setIsSaving(false);
        }
    }, [rows, existingEntry, projectId, setDynamicDataMutation, httpClient, refetch]);

    /* ── Navigation guard ───────────────────────────────────────── */
    const handleBack = useCallback(() => {
        if (isDirty && !window.confirm('You have unsaved changes. Leave without saving?')) return;
        onBack();
    }, [isDirty, onBack]);

    /* ── Filtered rows ──────────────────────────────────────────── */
    const filteredRows = useMemo(() => {
        if (!filterText.trim()) return rows.map((r, i) => ({ row: r, idx: i }));
        const q = filterText.toLowerCase();
        return rows
            .map((r, i) => ({ row: r, idx: i }))
            .filter(({ row }) => Object.values(row).some(v => v?.toLowerCase().includes(q)));
    }, [rows, filterText]);

    const isLoading = queryLoading || isLoadingBlob;

    /* ── Render ─────────────────────────────────────────────────── */
    return (
        <>
            {showPasteModal && (
                <PasteModal
                    existingColumns={columns}
                    onImport={handlePasteImport}
                    onClose={() => setShowPasteModal(false)}
                />
            )}

            <div className="flex flex-col h-full" style={style} data-testid="ParameterMappingStandard">

                {/* Toolbar */}
                <div
                    className="flex items-center gap-3 px-4 py-2.5 border-b flex-shrink-0"
                    style={{ borderColor: 'var(--border-default)' }}
                >
                    <button
                        className="flex items-center gap-1 text-sm font-medium px-2 py-1 rounded-md"
                        style={{ color: 'var(--accent-text)', background: 'none', border: 'none', cursor: 'pointer' }}
                        onClick={handleBack}
                    >
                        <ArrowLeft size={15} /> Back
                    </button>
                    <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                        Parameter Mapping
                    </span>
                    <div className="flex-1" />
                    {saveStatus === 'saved' && (
                        <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--success, #16a34a)' }}>
                            <Check size={13} /> Saved
                        </span>
                    )}
                    {saveStatus === 'error' && (
                        <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--danger)' }}>
                            <AlertCircle size={13} /> Save failed
                        </span>
                    )}
                    {isDirty && saveStatus === 'idle' && (
                        <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Unsaved changes</span>
                    )}
                    <button
                        className="btn-secondary flex items-center gap-1.5 text-sm"
                        onClick={() => setShowPasteModal(true)}
                    >
                        <ClipboardPaste size={14} /> Paste
                    </button>
                    <button
                        className="btn-primary flex items-center gap-2 text-sm"
                        disabled={!isDirty || isSaving}
                        onClick={handleSave}
                        style={{ opacity: !isDirty || isSaving ? 0.5 : 1 }}
                    >
                        {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                        Save
                    </button>
                </div>

                {/* Body */}
                {isLoading ? (
                    <div className="flex items-center justify-center flex-1">
                        <Loader2 size={20} className="animate-spin" style={{ color: 'var(--text-tertiary)' }} />
                    </div>
                ) : columns.length === 0 ? (
                    /* Empty state */
                    <div className="flex flex-col items-center justify-center flex-1 gap-4 px-6">
                        <div
                            className="rounded-xl p-10 text-center"
                            style={{ border: '2px dashed var(--border-default)', maxWidth: 420 }}
                        >
                            <p className="text-sm font-medium mb-1.5" style={{ color: 'var(--text-primary)' }}>No data yet</p>
                            <p className="text-xs mb-5" style={{ color: 'var(--text-tertiary)' }}>
                                Copy a table from Excel — column headers in the first row — then use Paste to import it.
                            </p>
                            <button
                                className="btn-primary flex items-center gap-2 text-sm mx-auto"
                                onClick={() => setShowPasteModal(true)}
                            >
                                <ClipboardPaste size={14} /> Paste from Excel
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col flex-1 min-h-0">

                        {/* Filter / count bar */}
                        <div
                            className="flex items-center gap-3 px-4 py-2 border-b flex-shrink-0"
                            style={{ borderColor: 'var(--border-default)' }}
                        >
                            <input
                                type="text"
                                className="input-base text-sm"
                                style={{ width: 240 }}
                                placeholder="Filter rows…"
                                value={filterText}
                                onChange={e => setFilterText(e.target.value)}
                            />
                            <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                                {filteredRows.length} / {rows.length} rows · {columns.length} columns
                            </span>
                        </div>

                        {/* Table */}
                        <div className="flex-1 overflow-auto">
                            <table
                                className="w-full"
                                style={{ borderCollapse: 'collapse', tableLayout: 'auto', fontSize: '0.8125rem' }}
                            >
                                <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                                    <tr style={{
                                        backgroundColor: 'var(--surface-1, var(--surface-100))',
                                        borderBottom: '2px solid var(--border-default)',
                                    }}>
                                        {columns.map(col => (
                                            <th
                                                key={col}
                                                className="text-left px-3 py-2 text-xs font-semibold select-none"
                                                style={{
                                                    color: 'var(--text-secondary)',
                                                    whiteSpace: 'nowrap',
                                                    borderRight: '1px solid var(--border-subtle, var(--border-default))',
                                                }}
                                            >
                                                {col}
                                            </th>
                                        ))}
                                        {/* Spacer for delete button column */}
                                        <th style={{ width: 36 }} />
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredRows.map(({ row, idx }) => (
                                        <TableRow
                                            key={idx}
                                            rowIdx={idx}
                                            row={row}
                                            columns={columns}
                                            onChange={handleCellChange}
                                            onRemove={handleRemoveRow}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Footer add-row strip */}
                        {!filterText && (
                            <div className="flex-shrink-0 border-t px-3 py-1" style={{ borderColor: 'var(--border-default)' }}>
                                <button
                                    className="flex items-center gap-1.5 text-xs w-full px-2 py-1.5 rounded"
                                    style={{
                                        color: 'var(--text-tertiary)', background: 'none',
                                        border: 'none', cursor: 'pointer', textAlign: 'left',
                                    }}
                                    onMouseEnter={e => {
                                        e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
                                        e.currentTarget.style.color = 'var(--text-primary)';
                                    }}
                                    onMouseLeave={e => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                        e.currentTarget.style.color = 'var(--text-tertiary)';
                                    }}
                                    onClick={handleAddRow}
                                >
                                    <Plus size={12} /> Add row
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default ParameterMappingStandard;
